/**
 * The workspace's trash (spec §8.22 "Trash", §13.9): pages that were soft-deleted, newest first,
 * each restorable. Hidden while the trash is empty, so a workspace that never deletes anything looks
 * exactly as it did. Needs the server; offline it simply does not appear.
 */
import { RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";
import { useRestorePage, useTrash } from "../api/hooks";
import { Button } from "../ui/Button";
import { useT } from "../i18n";

export function TrashPanel({ workspaceId, canRestore }: { workspaceId: string; canRestore: boolean }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const trash = useTrash(workspaceId);
  const restore = useRestorePage(workspaceId);
  const pages = trash.data ?? [];
  if (pages.length === 0) return null;
  return (
    <section className="mt-4" data-testid="trash">
      <button
        className="flex items-center gap-1.5 rounded-sm px-1 py-1 text-[13px] text-neutral-500 transition hover:text-neutral-900 dark:hover:text-neutral-100"
        aria-expanded={open}
        data-testid="trash-toggle"
        onClick={() => setOpen((o) => !o)}
      >
        <Trash2 size={14} /> {t("trash.title", { count: pages.length })}
      </button>
      {open && (
        <ul className="kss-surface mt-2 divide-y divide-neutral-200 dark:divide-neutral-800" data-testid="trash-list">
          {pages.map((p) => (
            <li key={p.id} className="flex items-center gap-2 px-4 py-2.5 text-sm" data-testid="trash-item" data-doc-id={p.id}>
              <span className="grow truncate">
                {p.title || t("page.untitled")}
                {p.deleted_at && <span className="ml-2 text-xs text-neutral-500">{t("trash.deletedOn", { date: new Date(p.deleted_at).toLocaleDateString() })}</span>}
              </span>
              {canRestore && (
                <Button size="sm" variant="secondary" data-testid="trash-restore" onClick={() => restore.mutate(p.id)}>
                  <RotateCcw size={13} /> {t("trash.restore")}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
